/**
 * What every block reader and every default renderer shares.
 *
 * **Extracted in Wave 8C**, when FR-I14 took the library from nine sections to
 * twenty-two. The thirteen new ones live in `library.tsx` and the original
 * nine in `index.tsx`, and both need the same four things — the context, the
 * link rule, the section wrapper and the reserved image box. Putting them here
 * rather than in `index.tsx` is what keeps `library.tsx` from importing the
 * module that imports it: a runtime cycle between two files that both define
 * renderers is the kind of thing that works in the test runner and fails in
 * the SSR bundle.
 *
 * The one import that does point back at `index.tsx` is `SectionRenderers`,
 * and it is `import type` — erased at build, so no cycle exists at runtime.
 */

import type { ReactNode } from 'react'

import { href } from '../read'
import type { SectionRenderers } from './index'
import type { BlogIndex } from '../types'

export interface BlockContext {
  /** FR-I4: false hides every price on the public site (T-4.10). */
  showPricing: boolean
  /** FR-I8, so a hero's call to action can point at the portal. */
  portalHref: string
  /**
   * w4e — FR-I6. Where the `contact` block's enquiry form posts, from
   * `payload.forms.enquiry`. **Null on a payload from a service that predates
   * it**, and the block then renders no form rather than one that posts
   * nowhere: 3D's seam comment is the rule, and a form that silently drops an
   * enquiry is worse than no form. FR-I14's `lead_form` obeys the same rule.
   */
  enquiryAction: string | null
  /**
   * The path being rendered, sent with the enquiry so the lead records the page
   * it came from (FR-H1) and so the service's 303 returns here. Validated on
   * the service before it is echoed into a `Location`.
   */
  pagePath: string
  /**
   * The firm's name, for decoration derived from it — a template's monogram in
   * an empty hero (ADR-0020 §6). Optional: absent, a renderer draws none.
   */
  siteName?: string
  /** The resolved template's own renderers, if it has any (ADR-0020). */
  sections?: SectionRenderers
  /**
   * `/_demo/<code>` on a template demo, empty on a firm's own domain — the
   * value `layout.tsx` already prefixes the navigation with. Optional, and
   * absent means empty, which is every firm's real site.
   */
  basePath?: string
  /**
   * The five switches of ADR-0014, resolved and failing closed
   * (`site/policy.ts`). **FR-I14 gave three of them something to suppress**:
   * `show_testimonials` takes `testimonial_strip`, `show_client_names` takes
   * `logo_strip` and `story_strip` (ADR-0022 §6).
   *
   * The service already drops those sections from the payload — a suppressed
   * testimonial that travelled to the browser would be in the page source,
   * which is where anyone looking would look. This is the **second** guard, on
   * the same argument `blog_list` reads `count` twice for: a payload built
   * from a fixture, from a sample, or by a caller that resolves blocks
   * differently must honour what the firm's regulator requires, and a rule
   * that lives in exactly one place is a rule this half cannot test.
   *
   * Optional, and **absent suppresses** — the same direction as every other
   * fail-closed read in this app.
   */
  switches?: Record<string, boolean>
  /**
   * The index of the block that draws the enquiry form's three `:target`
   * notices, when the page carries more than one form (T-4.28). Undefined
   * means "draw them", which is what a lone `contact` block has always done.
   */
  noticesAtIndex?: number
  /**
   * C17 — the render instant a `countdown` counts from (ADR-0036 §2). Set by
   * `renderSite` from `RenderOptions.now`, or the moment of the request.
   * Absent — a caller that renders no countdown — reads as now.
   */
  now?: Date
  /**
   * **The blog page's own index** — C24 item 15, ADR-0050 §2.
   *
   * Set by `renderSite` only on a render of `/blog`, from `payload.blog`, and
   * absent on every other page. `blog_list` reads it and becomes the index;
   * without it the block is the short list a home page wants.
   *
   * It is on the context and not in the block's props because the *page's*
   * address carries the filter and the page number — the block has no idea
   * which URL it is being drawn at, and nor should it.
   */
  blogIndex?: BlogIndex | null
}

/**
 * C17, ADR-0036 §7.1 and I-3 — **what every block view may carry**: the
 * section's address on its page, which `renderBlock` sets as the id of the
 * section's root. A theme renderer never sets its own id on the root; the
 * three fixed ids the shared and themed renderers draw (`services`, `team`,
 * `contact`) yield to it.
 */
export interface SectionAnchor {
  anchor?: string
}

const ANCHOR_OK = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

/**
 * A block's anchor, read defensively: the service already holds it to a slug
 * of at most forty characters, and this half never trusts a shape it did not
 * produce — an id that is not a slug is an id a `#fragment` cannot reach.
 */
export function readAnchor(props: Record<string, unknown>): string | null {
  const raw = typeof props.anchor === 'string' ? props.anchor.trim().toLowerCase() : ''
  return raw && raw.length <= 40 && ANCHOR_OK.test(raw) ? raw : null
}

/**
 * A link a block carries, made to stay inside a demo.
 *
 * **Why this exists:** `basePath` reached the navigation and the brand and
 * nothing else, so every link a *block* drew — the hero's button (`/contact`),
 * each service card (`/services#…`), each blog post (`/blog/…`) — left the
 * demo for the platform's own origin, where it landed on the staff app: a
 * *"404 Not Found"* for most, and for `/services` the firm's **staff services
 * admin**, inside the gallery's preview. On a firm's domain `basePath` is
 * empty and this returns every link untouched.
 *
 * Only root-relative links move. An absolute URL, a protocol-relative one and
 * a fragment are the firm's to point wherever they point; `/accounts/`,
 * `/client`, `/proposal`, `/site-assets/` and `/site-media/` (ADR-0021's pictures) are
 * other surfaces, never pages of the site — a demo that prefixed a picture
 * would ask the platform host for it and get nothing.
 *
 * **Every link every section of FR-I14 draws goes through this** (T-4.26) —
 * a section's buttons (ADR-0041 §1; `/book/…` is the booking page's surface, C20), a `feature_grid` item's href, a `split_feature`'s
 * call to action and a `showcase`'s. That is a rule and not a convention: the
 * defect it prevents was found on a firm's live demo and cost a session.
 */
export function siteHref(link: string | null, ctx: BlockContext): string | null {
  const base = ctx.basePath ?? ''
  if (!link || !base || !link.startsWith('/') || link.startsWith('//')) return link
  if (/^\/(accounts|book|client|proposal|site-assets|site-media)(\/|$|\?|#)/.test(link)) return link
  if (link === base || link.startsWith(`${base}/`)) return link
  return link === '/' ? base : `${base}${link}`
}

/**
 * A link the firm typed, read and made demo-safe in one step.
 *
 * `href()` is what refuses a `javascript:` or `data:` URL (`site/read.ts`) and
 * `siteHref()` is what keeps a real one inside a demo. Every new section calls
 * this rather than the two separately, because the one that gets forgotten is
 * always the first.
 */
export function link(value: unknown, ctx: BlockContext): string | null {
  return siteHref(href(value), ctx)
}

/**
 * A label and a link, or `null` — the shape every call to action in the
 * library has. **Both or neither**: a button with no link is a dead control
 * and a link with no words is nothing to click, and rendering either is the
 * "something wrong rather than nothing" the section rules forbid.
 */
export function action(
  label: string | null,
  target: unknown,
  ctx: BlockContext,
): { label: string; href: string } | null {
  const to = link(target, ctx)
  return label && to ? { label, href: to } : null
}

/**
 * C20, ADR-0041 §1 (waves.md Wave 20 I-3) — **one of a section's buttons, as
 * the service resolved it**: `{label, href}`, nothing else. The service has
 * already dropped a button with no label, no link, a page not in this version
 * or a draft booking flow, and it adds none of its own.
 */
export interface SiteButton {
  label: string
  href: string
}

/**
 * A section's `buttons`, read — **at most two, primary then secondary**, each
 * with its label and a link that `link()` has made safe and demo-local. An
 * absent or malformed list is `[]`, and `[]` draws nothing: **no section draws
 * a button it was not given** (ADR-0041 §1), so there is no fallback here to a
 * login, a contact page or anything else.
 */
export function readButtons(value: unknown, ctx: BlockContext): SiteButton[] {
  if (!Array.isArray(value)) return []
  const out: SiteButton[] = []
  for (const raw of value) {
    if (!raw || typeof raw !== 'object') continue
    const item = raw as Record<string, unknown>
    const label = typeof item.label === 'string' ? item.label.trim() : ''
    // I-3 allows `tel:` beside http(s), mailto and a site path; `href()` is
    // the shared rule and does not, so a telephone button is read here — digits,
    // a plus and spacing only, which is nothing that can run.
    const given = typeof item.href === 'string' ? item.href.trim() : ''
    const to = /^tel:\+?[\d\s().-]{3,30}$/.test(given) ? given : link(item.href, ctx)
    if (label && to) out.push({ label, href: to })
    if (out.length === 2) break
  }
  return out
}

/**
 * The firm's buttons, drawn — the first `primary`, the second `ghost`, which
 * every theme styles in its own way. **Nothing at all for an empty list**: not
 * an empty paragraph, which a theme's spacing would still show as a gap.
 */
export function Buttons({
  buttons,
  className,
  trail,
}: {
  buttons: SiteButton[]
  className: string
  /** A theme's own decoration after the primary's words (Modern's arrow) — `aria-hidden` by the theme. */
  trail?: ReactNode
}) {
  if (buttons.length === 0) return null
  return (
    <p className={className}>
      {buttons.map((b, i) => (
        <a key={i} className={`button ${i === 0 ? 'primary' : 'ghost'}`} href={b.href}>
          {b.label}
          {i === 0 ? trail : null}
        </a>
      ))}
    </p>
  )
}

export function Section({
  id,
  className,
  heading,
  children,
}: {
  id?: string
  className: string
  heading?: string | null
  children: ReactNode
}) {
  return (
    <section className={className} {...(id ? { id } : {})}>
      <div className="wrap">
        {heading ? <h2 className="section-heading">{heading}</h2> : null}
        {children}
      </div>
    </section>
  )
}

/**
 * A card's picture — a service's or a post's (FR-I13). In a **fixed 16:9 box**
 * for the reason `About`'s picture is in a 4:3 one: the box exists before the
 * bytes do, so nothing below it jumps, and `object-fit: cover` crops rather
 * than distorts. Decoration beside a title that says the same thing, so the
 * alt text is empty.
 */
export function CardImage({ src: source }: { src: string }) {
  return (
    <div className="card-image">
      <img src={source} alt="" width={640} height={360} loading="lazy" decoding="async" />
    </div>
  )
}

/** `YYYY-MM-DD` → `14 August 2026`. An unparseable date is printed as it came. */
export function formatDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return value
  const date = new Date(`${value}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
}

/** The six fields `app/domain/site.py: CONTACT_FIELDS` stores — see
 *  `site/types.ts: Contact` for why it is six and not the three plus three
 *  this half read until Wave 4D. */
export interface ContactDetails {
  email: string | null
  phone: string | null
  whatsapp: string | null
  address: string | null
  hours: string | null
  map_url: string | null
}
